import React, { Suspense, useState } from 'react';
import { Container, Row, Col, ListGroup, Card, Nav, Spinner, Button, Alert, Badge } from 'react-bootstrap';
import {FaRegCalendarCheck, FaUsers, FaTags, FaFire } from 'react-icons/fa';
import PlanFeaturesSection from '../../components/PlanFeaturesSection';
import FeatureComparisonChart from '../../components/FeatureComparisonChart';
import { SparkleButton } from '../../components/SparkleEffect';
import useSectionObserver from '../../hooks/useSectionObserver';
import './ProPageContent.css'; // Styles for this component
import ProCta from './ProCta';
import { Link, useNavigate } from 'react-router-dom';

const TestimonialSlider = React.lazy(() => import('../../components/TestimonialSlider'));
const FAQSection = React.lazy(() => import('../../components/FAQSection'));


const ProPageContent = ({
  benefitsRef,
  valueRef,
  proofRef,
  faqRef,
  comparisonRef,
  scrollToRef,
  logoImages,
  viewerCount,
  handleCountdownComplete,
  spotsLeft,
  ctaButtonText,
  valueItems,
  totalRetailValue,
  testimonialsPro,
  proCaseStudiesData,
  trustBadgesData,
  detailedProFeatures,
  comparisonFeaturesList,
  featureDefinitions,
  detailedFaqs,
  defaultExpandedFaqKey, // New prop
  starterResourcesRef,
  onStayOnStarter,
  showStayOnStarter = false,
  payOverTimeLink,
  openProCheckout, // Receive the handler as a prop
  openProCheckoutLabel = 'Unlock Pro',
  openProCheckoutDisabled = false,
  pricingInfo, // ADDED: New prop
  isLoadingPricing, // ADDED: New prop
  bootcampRef, // ADDED: New prop for bootcamp section
}) => {
  
  // Section observer for analytics (optional, can be expanded)
  const observedSectionsRef = useSectionObserver([
    { ref: benefitsRef, eventName: 'UPGRADE_TO_PRO_BENEFITS_VIEWED' },
    { ref: valueRef, eventName: 'UPGRADE_TO_PRO_VALUE_VIEWED' },
    { ref: proofRef, eventName: 'UPGRADE_TO_PRO_PROOF_VIEWED' },
    { ref: bootcampRef, eventName: 'UPGRADE_TO_PRO_BOOTCAMP_VIEWED' },
    { ref: faqRef, eventName: 'UPGRADE_TO_PRO_FAQ_VIEWED' },
    { ref: comparisonRef, eventName: 'UPGRADE_TO_PRO_COMPARE_VIEWED' },
  ], 'UpgradeToProPage');

  const navigate = useNavigate();

  // Helper to prepend a dollar sign if missing
  const addDollarSign = (str) => {
    if (!str || str === 'TBD') return str;
    const withDollar = str.startsWith('$') ? str : `$${str}`;
    return withDollar.replace('/month', '/mo');
  };

  const getProductDisplayDetails = (slug) => {
    if (!pricingInfo || !pricingInfo.products) {
      return { baseFormatted: 'TBD', currentFormatted: null, name: slug === 'pro-plan-upfront' ? 'Pro Upfront' : 'Pro Monthly' }; // Fallback
    }
    const product = pricingInfo.products.find(p => p.slug === slug);
    if (!product) {
      return { baseFormatted: 'TBD', currentFormatted: null, name: slug === 'pro-plan-upfront' ? 'Pro Upfront' : 'Pro Monthly' }; // Fallback
    }

    const rawBase = product.basePrice?.formatted || 'TBD';
    const rawCurrent = product.discountedPrice?.formatted || product.basePrice?.formatted;

    return {
      name: product.name || (slug === 'pro-plan-upfront' ? 'Pro Plan Upfront Fee' : 'Pro Plan Monthly'),
      baseFormatted: addDollarSign(rawBase),
      currentFormatted: addDollarSign(rawCurrent),
      isDiscounted: !!product.discountedPrice && rawCurrent !== rawBase,
    };
  };

  const upfrontDetails = getProductDisplayDetails('pro-plan-upfront');
  const monthlyDetails = getProductDisplayDetails('pro-plan');

  const monthlyPriceString = monthlyDetails.currentFormatted || 'TBD'; // e.g., $199/mo

  // For savings calculation (assuming discount is only on upfront)
  const originalUpfrontAmount = pricingInfo?.products?.find(p => p.slug === 'pro-plan-upfront')?.basePrice?.amount || 0;
  const discountedUpfrontAmount = pricingInfo?.products?.find(p => p.slug === 'pro-plan-upfront')?.discountedPrice?.amount || originalUpfrontAmount;
  const savingsAmount = (originalUpfrontAmount - discountedUpfrontAmount) / 100;
  const savingsFormatted = savingsAmount > 0 ? `$${savingsAmount.toLocaleString('en-US', {minimumFractionDigits: 0, maximumFractionDigits: 0})}` : '';

  // --- BEGIN: Dynamic pricing helpers for UI sections ---
  const isUpfrontDiscounted = upfrontDetails.isDiscounted;
  const isMonthlyDiscounted = monthlyDetails.isDiscounted;
  
  // Sale detection: show sale if there are ANY real discounts (more permissive)
  const hasRealDiscounts = (isUpfrontDiscounted || isMonthlyDiscounted) && savingsAmount > 0;
  const isRealSaleActive = hasRealDiscounts; // Simplified - just check for actual savings

  const promoEndDateFormatted = pricingInfo?.activePromotion?.endDate
    ? new Date(pricingInfo.activePromotion.endDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    : null;

  const priceBlurb = `${upfrontDetails.currentFormatted}${isUpfrontDiscounted ? ` (was ${upfrontDetails.baseFormatted})` : ''} + ${monthlyDetails.currentFormatted?.replace('/mo','') || monthlyDetails.currentFormatted || ''}/mo${isMonthlyDiscounted ? ` (was ${monthlyDetails.baseFormatted})` : ''}`;
  // --- END: Dynamic pricing helpers ---

  const dynamicCtaButtonText = 'Unlock Pro';

  // Derive valueItems and totalRetailValue from pricingInfo or fallbacks
  const derivedValueItems = (Array.isArray(valueItems) && valueItems.length > 0)
    ? valueItems
    : (pricingInfo?.products?.length > 0
        ? pricingInfo.products.map(p => ({
            label: p.name || (p.slug === 'pro-plan-upfront' ? 'Pro Plan Upfront Fee' : 'Pro Plan Monthly Subscription'),
            value: p.discountedPrice?.formatted || p.basePrice?.formatted,
          }))
        : []);

  const derivedTotalRetailValue = (Array.isArray(valueItems) && valueItems.length > 0)
    ? totalRetailValue
    : (pricingInfo?.summary?.originalTotalAmount ? (pricingInfo.summary.originalTotalAmount / 100) : 0);

  // Enhanced benefits data with icons
  const enhancedBenefitsData = [
    {
      icon: "fas fa-robot",
      title: "Unleash AI Power",
      description: "Generate sophisticated trading bots in minutes. No coding, just your strategy in plain English, amplified by Pro AI capabilities."
    },
    {
      icon: "fas fa-chart-line", 
      title: "Access Elite Strategies",
      description: "Instantly use a library of 50+ proven, Pro-level algorithms for diverse markets. Continuously updated and expanded."
    },
    {
      icon: "fas fa-graduation-cap",
      title: "Expert Mentorship", 
      description: "Accelerate your learning with live Pro workshops, an intensive Bootcamp, and personalized 1-on-1 guidance from our top experts."
    },
    {
      icon: "fas fa-users",
      title: "Join the Pro Circle",
      description: "Network with serious traders, get priority support, and gain early access to new features within our exclusive Pro community."
    }
  ];

  // Logo data for enhanced showcase - use original logoImages prop
  const logoData = logoImages || [
    { src: "/images/logos/interactive-brokers-logo.png", alt: "Interactive Brokers" },
    { src: "/images/logos/alpaca-logo.png", alt: "Alpaca" },
    { src: "/images/logos/tradier-logo.png", alt: "Tradier" },
    { src: "/images/logos/coinbase-logo.png", alt: "Coinbase" },
    { src: "/images/logos/binance-logo.png", alt: "Binance" },
    { src: "/images/logos/charles-schwab-logo.png", alt: "Charles Schwab" }
  ];

  if (isLoadingPricing && !pricingInfo) { // Show full page spinner if no data yet
    return (
        <Container className="text-center py-5">
            <Spinner animation="border" role="status">
                <span className="visually-hidden">Loading Pricing...</span>
            </Spinner>
            <p className="mt-3">Loading pricing details...</p>
        </Container>
    );
  }

  return (
    <div className="pro-page-content">
      {/* Enhanced Social Proof Banner */}
      <div className="pro-page-content-panel-dark">
        <Container>
          <div className="pro-page-content-social-proof-enhanced">
            <div className="pro-page-content-social-proof-item">
              <div className="pro-page-content-social-proof-icon pro-page-content-social-proof-icon-live">
                <FaRegCalendarCheck />
              </div>
              <div className="pro-page-content-social-proof-content">
                <div className="pro-page-content-social-proof-number">{viewerCount || 11}+</div>
                <div className="pro-page-content-social-proof-label">traders viewing now</div>
              </div>
            </div>
            
            <div className="pro-page-content-social-proof-item">
              <div className="pro-page-content-social-proof-icon pro-page-content-social-proof-icon-urgency">
                <FaFire />
              </div>
              <div className="pro-page-content-social-proof-content">
                <div className="pro-page-content-social-proof-number">{spotsLeft || 5}</div>
                <div className="pro-page-content-social-proof-label">Pro spots left this month</div>
              </div>
            </div>
            
            <div className="pro-page-content-social-proof-item">
              <div className="pro-page-content-social-proof-icon pro-page-content-social-proof-icon-trust">
                <FaUsers />
              </div>
              <div className="pro-page-content-social-proof-content">
                <div className="pro-page-content-social-proof-number">1,000+</div>
                <div className="pro-page-content-social-proof-label">trusted traders</div>
              </div>
            </div>
          </div>
        </Container>
      </div>

      {/* Enhanced Logo Section */}
      <div className="pro-page-content-panel-light-accent">
        <Container>
          <div className="text-center mb-4">
            <h4 className="pro-page-content-logo-headline">
              <i className="fas fa-handshake me-2 text-primary"></i>
              Works with Your Favorite Brokers & Exchanges
            </h4>
            <p className="pro-page-content-logo-subtext">
              Seamlessly connect with top trading platforms
            </p>
          </div>
          <div className="pro-page-content-logo-showcase">
            {logoData.map((logo, index) => (
              <div key={index} className="pro-page-content-logo-item">
                <img 
                  src={logo.src} 
                  alt={logo.alt} 
                  className="pro-page-content-logo-enhanced"
                />
                <div className="pro-page-content-logo-overlay">
                  <i className="fas fa-check-circle"></i>
                </div>
              </div>
            ))}
            <div className="pro-page-content-logo-item pro-page-content-logo-more">
              <div className="pro-page-content-logo-more-content">
                <i className="fas fa-plus-circle"></i>
                <span>And More</span>
              </div>
            </div>
          </div>
        </Container>
      </div>

      {/* --- Jump Navigation (moved higher up) --- */}
      <Nav className="sticky-top pro-page-content-jump-nav d-none d-lg-flex flex-column" style={{ top: '50px', zIndex: 1019, backgroundColor: '#161b22', padding: '0.5rem 0' }}>
        {/* Links Row */}
        <div className="d-flex justify-content-center flex-wrap mb-2">
          <Nav.Item><Nav.Link onClick={() => scrollToRef(benefitsRef)} className="pro-page-content-jump-nav-link">Why Pro?</Nav.Link></Nav.Item>
          <Nav.Item><Nav.Link onClick={() => scrollToRef(valueRef)} className="pro-page-content-jump-nav-link">Value</Nav.Link></Nav.Item>
          <Nav.Item><Nav.Link onClick={() => scrollToRef(comparisonRef)} className="pro-page-content-jump-nav-link">Compare</Nav.Link></Nav.Item>
          <Nav.Item><Nav.Link onClick={() => scrollToRef(proofRef)} className="pro-page-content-jump-nav-link">Proof</Nav.Link></Nav.Item>
          <Nav.Item><Nav.Link onClick={() => scrollToRef(bootcampRef)} className="pro-page-content-jump-nav-link">Live Algo Lab</Nav.Link></Nav.Item>
          <Nav.Item><Nav.Link onClick={() => scrollToRef(faqRef)} className="pro-page-content-jump-nav-link">FAQs</Nav.Link></Nav.Item>
        </div>
      </Nav>

      {/* Enhanced Benefits Section */}
      <div ref={benefitsRef} className="pro-page-content-panel-dark">
        <Container>
          <div className="text-center mb-5">
            <h2 className="pro-page-content-section-title">
              Why Go Pro? The <span className="pro-page-content-highlight">Undeniable Advantages</span>
            </h2>
          </div>
          <Row>
            {enhancedBenefitsData.map((benefit, index) => (
              <Col lg={3} md={6} className="mb-4" key={index}>
                <div className="pro-page-content-benefit-card">
                  <div className="pro-page-content-benefit-icon">
                    <i className={benefit.icon}></i>
                  </div>
                  <h4 className="pro-page-content-benefit-title">{benefit.title}</h4>
                  <p className="pro-page-content-benefit-description">{benefit.description}</p>
                  <div className="pro-page-content-benefit-glow"></div>
                </div>
              </Col>
            ))}
          </Row>
        </Container>
      </div>

      {/* Enhanced Active Promotion Alert */}
      {isRealSaleActive && (
        <div className="pro-page-content-mega-sale-section">
          <Container>
            <div className="pro-page-content-mega-sale-alert">
              <div className="pro-page-content-sale-header">
                <FaFire className="pro-page-content-sale-icon" />
                <h3>Limited Time Sale</h3>
                <FaFire className="pro-page-content-sale-icon" />
              </div>
              
              <div className="pro-page-content-price-comparison">
                {isUpfrontDiscounted && (
                  <div className="pro-page-content-old-price">
                    <span className="pro-page-content-strikethrough">
                      {upfrontDetails.baseFormatted} + {monthlyDetails.baseFormatted}
                    </span>
                  </div>
                )}
                <div className="pro-page-content-new-price">
                  {upfrontDetails.currentFormatted} + {monthlyDetails.currentFormatted}
                </div>
                {savingsFormatted && (
                  <div className="pro-page-content-savings-text">
                    You Save {savingsFormatted}!
                  </div>
                )}
              </div>
              
              <div className="pro-page-content-sale-cta-section">
                {promoEndDateFormatted && (
                  <div className="pro-page-content-sale-countdown">
                    <i className="fas fa-clock me-2"></i>
                    Offer ends {promoEndDateFormatted}
                  </div>
                )}
                
                <SparkleButton 
                  className="pro-page-content-sale-cta"
                  onClick={openProCheckout}
                >
                  <i className="fas fa-bolt me-2"></i>
                  Claim This Deal Now
                </SparkleButton>
              </div>
            </div>
          </Container>
        </div>
      )}

      {/* Regular Pricing Section - Shows when there's no sale */}
      {!isRealSaleActive && (
        <div className="pro-page-content-regular-pricing-section">
          <Container>
            <div className="pro-page-content-regular-pricing-alert">
              <div className="pro-page-content-pricing-header">
                <i className="fas fa-star me-2"></i>
                <h3>Pro Pricing</h3>
                <i className="fas fa-star me-2"></i>
              </div>
              
              <div className="pro-page-content-current-pricing">
                <div className="pro-page-content-pricing-display">
                  {upfrontDetails.currentFormatted} + {monthlyDetails.currentFormatted}
                </div>
                <div className="pro-page-content-pricing-description">
                  One-time setup fee + monthly subscription
                </div>
              </div>
              
              <div className="pro-page-content-pricing-cta-section">
                <SparkleButton 
                  className="pro-page-content-pricing-cta"
                  onClick={openProCheckout}
                  disabled={openProCheckoutDisabled}
                >
                  <i className="fas fa-rocket me-2"></i>
                  {openProCheckoutLabel}
                </SparkleButton>
              </div>
            </div>
          </Container>
        </div>
      )}

      {/* Enhanced CTA Section */}
      <div className="pro-page-content-mega-cta-section">
        <Container>
          <div className="text-center">
            <h2 className="pro-page-content-mega-cta-title">
              Ready to unlock Pro? <span className="pro-page-content-highlight-gradient">Get full access today</span>
            </h2>
            <div className="pro-page-content-cta-buttons-enhanced">
              <SparkleButton 
                className="pro-page-content-mega-cta-button"
                onClick={openProCheckout}
                disabled={openProCheckoutDisabled}
              >
                <i className="fas fa-rocket me-2"></i>
                {openProCheckoutLabel || ctaButtonText}
                <div className="pro-page-content-button-shine"></div>
              </SparkleButton>
            </div>
          </div>
        </Container>
      </div>

      {/* Enhanced Urgency Section */}
      <div className="pro-page-content-urgency-section-enhanced">
        <Container>
          <div className="text-center">
            {isRealSaleActive ? (
              <>
                <div className="pro-page-content-urgency-badge">
                  <i className="fas fa-bolt me-2"></i>
                  SALE ENDING SOON
                </div>
                <h2 className="pro-page-content-urgency-title">
                  🔥 Pro Sale Ends Soon!
                </h2>
              </>
            ) : (
              <>
                <div className="pro-page-content-urgency-badge pro-page-content-urgency-badge-no-sale">
                  <i className="fas fa-star me-2"></i>
                  LIMITED AVAILABILITY
                </div>
                <h2 className="pro-page-content-urgency-title">
                  ⚡ Upgrade to Pro Today!
                </h2>
              </>
            )}
            
            <div className="pro-page-content-scarcity-indicators">
              <div className="pro-page-content-scarcity-item">
                <i className="fas fa-users text-warning"></i>
                <span>Only <strong>{spotsLeft || 5} Pro spots</strong> available this month.</span>
              </div>
            </div>
            
            {/* --- Improved Pricing Display --- */}
            <div className="pro-page-content-pricing-showcase">
              {isRealSaleActive && isUpfrontDiscounted && (
                <div className="pro-page-content-price-strike">
                  <span className="pro-page-content-was-label">Previously:</span>
                  <span className="pro-page-content-strikethrough">
                    {upfrontDetails.baseFormatted} + {monthlyDetails.baseFormatted}
                  </span>
                </div>
              )}
              <div className="pro-page-content-price-current">
                <span className="pro-page-content-now-label">{isRealSaleActive ? 'Now:' : 'Pro Pricing:'}</span>
                {upfrontDetails.currentFormatted} + {monthlyDetails.currentFormatted}
              </div>
              {isRealSaleActive && savingsFormatted && (
                <div className="pro-page-content-savings-badge">
                  You Save {savingsFormatted}!
                </div>
              )}
            </div>
            
            {/* --- Countdown and CTA --- */}
            <div className="pro-page-content-cta-section">
              {isRealSaleActive && promoEndDateFormatted && (
                <div className="pro-page-content-sale-countdown">
                  <i className="fas fa-clock me-2"></i>
                  Offer ends {promoEndDateFormatted}
                </div>
              )}
              
              <SparkleButton 
                className="pro-page-content-urgency-cta"
                onClick={openProCheckout}
                disabled={openProCheckoutDisabled}
              >
                <i className={isRealSaleActive ? "fas fa-lightning-bolt me-2" : "fas fa-rocket me-2"}></i>
                {isRealSaleActive ? openProCheckoutLabel : openProCheckoutLabel}
              </SparkleButton>
              
              <div className="pro-page-content-guarantee-enhanced">
                <div className="pro-page-content-guarantee-content">
                  <div className="pro-page-content-guarantee-title">Cancel anytime</div>
                  <div className="pro-page-content-guarantee-text">
                    Manage your subscription from your account. Cancellation stops future monthly billing.
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Container>
      </div>

      {/* Value Proposition Section - Enhanced */}
      <div ref={valueRef} className="pro-page-content-panel-dark">
        <Container>
          <div className="text-center mb-5">
            <h2 className="pro-page-content-section-title">
              Unmatched Value: Get Over <span className="pro-page-content-value-highlight">${derivedTotalRetailValue.toLocaleString()}</span> in Tools & Resources
            </h2>
          </div>
          
          <Row className="justify-content-center">
            <Col lg={8}>
              <Card className="pro-value-showcase-card">
                <Card.Header className="pro-value-showcase-header">
                  Here's What You Get with Pro:
                </Card.Header>
                <Card.Body className="pro-value-showcase-body">
                  <ListGroup variant="flush" className="pro-value-showcase-list">
                    {derivedValueItems.map((item, index) => (
                      <ListGroup.Item key={index} className="pro-value-showcase-item">
                        <div className="d-flex align-items-center">
                          <i className="fas fa-check-circle pro-value-showcase-check"></i>
                          <span className="pro-value-showcase-content">{item.label}</span>
                        </div>
                        <span className="pro-value-showcase-price">{item.value}</span>
                      </ListGroup.Item>
                    ))}
                  </ListGroup>
                  
                  <div className="pro-value-showcase-total">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <strong>Total Retail Value:</strong>
                      <strong className="pro-page-content-strikethrough">${derivedTotalRetailValue.toLocaleString()}</strong>
                    </div>
                    
                    <div className="pro-value-showcase-pricing-section">
                      <div className="pro-value-showcase-your-price-label">Your Pro Price:</div>
                      <div className="pro-value-showcase-your-price-amount">
                        {upfrontDetails.currentFormatted} + {monthlyDetails.currentFormatted}
                      </div>
                      
                      {(() => {
                        const currentUpfrontAmount = upfrontDetails.currentFormatted?.replace('$', '').replace(',', '') || '0';
                        const currentMonthlyAmount = monthlyDetails.currentFormatted?.replace('$', '').replace('/mo', '').replace('/month', '') || '0';
                        const totalCurrentPrice = parseFloat(currentUpfrontAmount) + (parseFloat(currentMonthlyAmount) * 12); // Annual comparison
                        const totalSavings = derivedTotalRetailValue - totalCurrentPrice;
                        const savingsPercentage = Math.round((totalSavings / derivedTotalRetailValue) * 100);
                        
                        return totalSavings > 0 ? (
                          <div className="pro-value-showcase-savings">
                            You Save ${totalSavings.toLocaleString()} ({savingsPercentage}% off!)
                          </div>
                        ) : null;
                      })()}
                    </div>
                  </div>
                  
                  <div className="pro-value-showcase-cta-section">
                <SparkleButton 
                  className="pro-page-content-value-cta"
                  onClick={openProCheckout}
                  disabled={openProCheckoutDisabled}
                >
                  <i className="fas fa-star me-2"></i>
                  {openProCheckoutLabel}
                </SparkleButton>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>

      {/* Rest of existing sections... */}
      {/* Keep existing comparison, testimonials, FAQ sections but with enhanced styling */}
       
       {/* --- Plan Comparison Section (moved up) --- */}
       <div ref={comparisonRef} className="pro-page-content-panel-light-accent">
         <Container className="py-3">
           <h2 className="text-center mb-4 pro-page-content-section-title">Compare: Starter vs. <span className="pro-page-content-highlight">Pro</span></h2>
           <FeatureComparisonChart 
             comparisonFeaturesList={comparisonFeaturesList}
             featureDefinitions={featureDefinitions}
             title="Starter vs. Pro"
             leftColumnLabel="Starter Plan"
             rightColumnLabel="Pro Plan"
             renderCTA={() => (
               <ProCta
                 label={openProCheckoutLabel}
                 onClick={openProCheckout}
                 className="sales-cta-button primary-action"
                 size="lg"
                 aria-label="Upgrade to Pro Now - Comparison Table CTA"
                 disabled={openProCheckoutDisabled}
               />
             )}
           />
         </Container>
       </div>

       {/* --- Proof Section (Testimonials, Case Studies, Trust Badges) --- */}
       <div ref={proofRef} className="pro-page-content-panel-light-accent">
         <Container className="py-3">
           <h2 className="text-center mb-5 pro-page-content-section-title">Real Traders, <span className="pro-page-content-highlight">Real Pro Results</span></h2>
           {/* Testimonials */}
           <Suspense fallback={<div className="text-center"><Spinner animation="border" /> Loading Testimonials...</div>}>
             <TestimonialSlider testimonials={testimonialsPro} />
           </Suspense>
           {/* Case Studies */}
           <h3 className="text-center mt-5 mb-4 pro-page-content-subsection-title">Pro Member Success Stories</h3>
           <Row>
             {proCaseStudiesData.map((card, idx) => (
               <Col md={4} key={idx} className="mb-4">
                 <div className="pro-page-content-case-study-card card h-100">
                   <img
                     src={card.img}
                     alt={card.alt}
                     className="card-img-top"
                     style={{ objectFit: 'cover', width: '100%', aspectRatio: '16/9', borderRadius: '8px 8px 0 0' }}
                   />
                   <div className="card-body">
                     <h5 className="card-title">{card.title}</h5>
                     <p className="card-text">{card.body}</p>
                   </div>
                 </div>
               </Col>
             ))}
           </Row>
           {/* Trust Badges */}
           <Row className="text-center mt-5 pro-page-content-trust-badges-section">
             {trustBadgesData.map((badge, index) => (
               <Col md={4} key={index} className="mb-3">
                 <div className="pro-page-content-trust-badge">
                   <div className="pro-page-content-trust-badge-value">{badge.value}</div>
                   <div className="pro-page-content-trust-badge-label">{badge.label}</div>
                 </div>
               </Col>
             ))}
           </Row>
         </Container>
       </div>

       {/* --- Everything Included in Pro Membership --- */}
       <div className="pro-page-content-panel-dark">
         <Container className="py-3">
           <h2 className="text-center mb-5 pro-page-content-section-title">Everything Included in Your <span className="pro-page-content-highlight">Pro Membership</span></h2>
           <PlanFeaturesSection features={detailedProFeatures} featuresSource="proPageList" />
         </Container>
       </div>

       {/* --- Live Algo Lab Section --- */}
       <div ref={bootcampRef} className="pro-page-content-panel-light-accent">
         <Container className="py-3">
           <h2 className="text-center mb-3 pro-page-content-section-title">
             <span className="pro-page-content-highlight">Exclusive Pro Benefit:</span> Live Algo Lab
           </h2>
           <p className="text-center mb-4 pro-page-bootcamp-description">
             Join Rob live on Tuesdays, influence what gets built, get unstuck during implementation, and use the organized member archive between sessions.
           </p>
           <div className="text-center">
             <Button
               className="bootcamp-cta-button"
               onClick={openProCheckout}
               disabled={openProCheckoutDisabled}
             >
               <FaRegCalendarCheck className="me-2" />
               {openProCheckoutLabel || 'Unlock Pro'}
             </Button>
             <p className="bootcamp-cta-subtext mt-3">
               AI Bootcamp is a separate live or self-paced course product. Pro includes Live Algo Lab.
             </p>
             <Link to="/live-algo-lab" className="btn btn-outline-light mt-3">
               Preview Live Algo Lab
             </Link>
           </div>
         </Container>
       </div>
       
       {/* --- FAQ Section --- */}
       <div ref={faqRef} className="pro-page-content-panel-dark">
         <Container className="py-3">
           <h2 className="text-center mb-3 pro-page-content-section-title">Frequently Asked <span className="pro-page-content-highlight">Questions</span></h2>
           <Suspense fallback={<div className="text-center"><Spinner animation="border" /> Loading FAQs...</div>}>
             <FAQSection 
               faqs={detailedFaqs} 
               defaultActiveKeyProp={defaultExpandedFaqKey}
             />
           </Suspense>
         </Container>
       </div>

       {/* --- Final CTA Section --- */}
       <div className="upgrade-to-pro_page-section upgrade-to-pro-page-panel-dark text-center py-5">
         <Container>
           <Row className="justify-content-center">
             <Col md={8}>
               <h2 className="mb-4 text-white">Ready to Elevate Your Trading?</h2>
               <p className="lead mb-4 text-white-50">
                 Join thousands of successful traders who have transformed their strategies with Lumiwealth Pro.
                 Don't miss out on unlimited AI power, exclusive strategies, and expert mentorship.
               </p>
               <SparkleButton
                 onClick={openProCheckout}
                 className="upgrade-to-pro-page-final-cta"
                 disabled={openProCheckoutDisabled}
               >
                 <i className="fas fa-rocket me-2"></i>
                 {openProCheckoutLabel || dynamicCtaButtonText.replace("Upgrade Now for", "Yes, Upgrade Me to Pro for")}
               </SparkleButton>
               {/* Stay on Starter - subtle alternative at bottom */}
               {showStayOnStarter && onStayOnStarter && (
                 <div className="mt-4 pt-3" style={{ borderTop: '1px solid rgba(255,255,255,0.1)' }}>
                   <button 
                     className="btn btn-link text-muted p-0"
                     onClick={onStayOnStarter}
                     style={{ 
                       fontSize: '0.875rem',
                       textDecoration: 'none',
                       color: 'rgba(255,255,255,0.6) !important'
                     }}
                   >
                     Not ready? Continue with Starter →
                   </button>
                 </div>
               )}
             </Col>
           </Row>
         </Container>
       </div>
     </div>
   );
 };

export default ProPageContent;
